import { useMemo } from "react";
import { useSearchParams } from "react-router";

import { External, EmptystateSearchNoResultsColored } from "components/icons/generated";
import NotFoundPage from "components/error/NotFoundPage";
import PageLayoutSkeleton from "components/PageLayoutSkeleton";
import SearchInput from "components/SearchInput";
import { getSearchQuery } from "components/SearchInput/helpers";
import { SEARCH_PLACEHOLDERS } from "constants/searchPlaceholders";
import Button from "ds/components/Button";
import LinkButton from "ds/components/Button/LinkButton";
import EmptyState from "ds/components/EmptyState";
import TableSimple from "ds/components/TableSimple";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import useFlowsIntegration from "shared/Flows/useFlowsIntegration";
import useFlowsProjects from "shared/Flows/Projects/useFlowsProjects";
import { AccountContext } from "views/AccountWrapper";

import FLOWS_PROJECTS_CELLS from "./Cells";
import { FLOWS_PROJECTS_FILTERS_ORDER_KEY } from "./constants";
import { showFlowsDetailsDrawer } from "./DetailsDrawer";
import FlowsProjectsEmpty from "./Empty";
import { filterFlowsProjectsByName, sortFlowsProjects } from "./helpers";
import FlowsProjectsSectionHeader from "./SectionHeader";
import { FlowsProjectRow } from "./types";
import FlowsProjectsUnlinked from "./Unlinked";
import useFlowsProjectsTableStore from "./useStore";
import FlowsProjectsViewHeader from "./ViewHeader";

const TABLE_ARIA_LABEL = "Flows projects table";

const FlowsProjectsList = () => {
  const { accountName, viewer } = useTypedContext(AccountContext);
  const [searchParams] = useSearchParams();

  useTitle(`Flows · ${accountName}`);

  const { flowsIntegration, loading: integrationLoading } = useFlowsIntegration();
  const { projects, loading: projectsLoading, error } = useFlowsProjects();

  const rows = useMemo<FlowsProjectRow[]>(
    () =>
      projects.map((project) => ({
        id: project.id,
        name: project.name,
        assignedRoles: project.roleBindings.length,
      })),
    [projects]
  );

  const filteredProjects = useMemo(() => {
    const searchQuery = getSearchQuery(searchParams);
    const filtered = filterFlowsProjectsByName(rows, searchQuery);

    return sortFlowsProjects(searchParams, filtered);
  }, [rows, searchParams]);

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  // The Flows projects area exposes every project, so it is account-admin only.
  if (!viewer.admin) {
    return <NotFoundPage />;
  }

  if (integrationLoading || projectsLoading) {
    return <PageLayoutSkeleton />;
  }

  if (!flowsIntegration) {
    return (
      <>
        <FlowsProjectsViewHeader />
        <FlowsProjectsUnlinked />
      </>
    );
  }

  const hasProjects = rows.length > 0;

  return (
    <>
      <FlowsProjectsViewHeader />

      <FlowsProjectsSectionHeader>
        {hasProjects && (
          <SearchInput
            filtersOrderSettingsKey={FLOWS_PROJECTS_FILTERS_ORDER_KEY}
            placeholder={SEARCH_PLACEHOLDERS.FLOWS_PROJECTS}
          />
        )}
        <Button
          variant="secondary"
          onPress={() =>
            showFlowsDetailsDrawer({
              baseURL: flowsIntegration.baseURL,
              organizationName: flowsIntegration.organizationName,
              createdAt: flowsIntegration.createdAt,
              updatedAt: flowsIntegration.updatedAt,
            })
          }
        >
          Details
        </Button>
        <LinkButton variant="primary" endIcon={External} href="/flows/login" target="_blank">
          Open Flows
        </LinkButton>
      </FlowsProjectsSectionHeader>

      {!hasProjects && <FlowsProjectsEmpty />}
      {hasProjects && !filteredProjects.length && (
        <EmptyState
          title="No results"
          caption="Don't give up! Check the spelling or try something less specific."
          icon={EmptystateSearchNoResultsColored}
          announce
        />
      )}
      {!!filteredProjects.length && (
        <TableSimple
          data={filteredProjects}
          store={useFlowsProjectsTableStore}
          ariaLabel={TABLE_ARIA_LABEL}
          cellPerColumn={FLOWS_PROJECTS_CELLS}
          filtersStorageKey={FLOWS_PROJECTS_FILTERS_ORDER_KEY}
          hasPagination={false}
          stickyRight={() => false}
          hideSettings
        />
      )}
    </>
  );
};

export default FlowsProjectsList;
